import { useQuery } from '@tanstack/react-query'
import { ApiClientError } from '@/lib/api-client'
import { orgFetch } from '@/lib/org-api'
import { useAuthStore } from '@/lib/auth-store'
import { PageHeader } from '@/components/PageHeader'
import { DataTable } from '@/components/DataTable'
import { AsyncBoundary } from '@/components/AsyncBoundary'

interface OrgArtistRow {
  id: string
  name: string
  slug: string
  showsConfirmedCount: number
  pipelineOpenCount: number
}

interface OrgStats {
  artistCount: number
  showsThisYear: number
  showsNextYear: number
  pipelineValueMinor: number
  pipelineOpenCount: number
  brokersAtRisk: number
}

export function RosterPage() {
  const user = useAuthStore((s) => s.user)
  const orgName = user?.organisationId ? 'Roster' : 'No organisation'

  const artistsQuery = useQuery<OrgArtistRow[], ApiClientError>({
    queryKey: ['org-artists'],
    queryFn: () => orgFetch<OrgArtistRow[]>('/artists'),
    enabled: !!user?.organisationId,
  })

  const statsQuery = useQuery<OrgStats, ApiClientError>({
    queryKey: ['org-stats'],
    queryFn: () => orgFetch<OrgStats>('/stats'),
    enabled: !!user?.organisationId,
  })

  if (!user?.organisationId) {
    return (
      <div>
        <PageHeader title={orgName} />
        <div className="rounded-md border border-ovation-ink/10 bg-white p-6 text-sm text-ovation-muted">
          You're not part of an organisation. Roster features are only available
          to org members.
        </div>
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title="Roster"
        subtitle="Cross-artist view across every artist in your organisation."
      />

      {statsQuery.data && (
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          <StatCard label="Artists" value={statsQuery.data.artistCount} />
          <StatCard label="Shows this year" value={statsQuery.data.showsThisYear} />
          <StatCard label="Shows next year" value={statsQuery.data.showsNextYear} />
          <StatCard
            label="Open pipeline"
            value={statsQuery.data.pipelineOpenCount}
          />
          <StatCard
            label="Pipeline value"
            value={`£${(statsQuery.data.pipelineValueMinor / 100).toLocaleString()}`}
          />
          <StatCard
            label="Brokers at risk"
            value={statsQuery.data.brokersAtRisk}
            tone={statsQuery.data.brokersAtRisk > 0 ? 'warning' : undefined}
          />
        </div>
      )}

      <AsyncBoundary isLoading={artistsQuery.isLoading} error={artistsQuery.error}>
        <DataTable
          rows={artistsQuery.data ?? []}
          rowKey={(r) => r.id}
          emptyMessage="No artists in this organisation yet."
          columns={[
            { key: 'name', header: 'Artist', render: (r) => r.name },
            { key: 'slug', header: 'Slug', render: (r) => <code className="text-xs">{r.slug}</code> },
            {
              key: 'shows',
              header: 'Confirmed shows',
              align: 'right',
              render: (r) => r.showsConfirmedCount,
            },
            {
              key: 'pipeline',
              header: 'Open pipeline',
              align: 'right',
              render: (r) => r.pipelineOpenCount,
            },
          ]}
        />
      </AsyncBoundary>
    </div>
  )
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string
  value: string | number
  tone?: 'warning' | undefined
}) {
  return (
    <div
      className={`rounded-lg border bg-white p-5 ${
        tone === 'warning'
          ? 'border-amber-200 bg-amber-50'
          : 'border-ovation-ink/10'
      }`}
    >
      <div className="text-xs uppercase tracking-wider text-ovation-muted">
        {label}
      </div>
      <div className="mt-2 font-display text-3xl">{value}</div>
    </div>
  )
}
